import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader

import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Carousel } from 'react-responsive-carousel';
import { useParallax } from 'react-scroll-parallax';
import styled from 'styled-components';

import { MagicWandSvg } from '../assets/icons/MagicWandSvg';
import Certificate from '../assets/images/LandingPage/Certificate.png';
import SchoolLogo7 from '../assets/images/LandingPage/CISDlogo.png';
import SchoolLogo4 from '../assets/images/LandingPage/DALTONlogo.png';
import SchoolLogo6 from '../assets/images/LandingPage/DELAWARElogo.png';
import DrawingImage from '../assets/images/LandingPage/drawing.webp';
import GetCertified from '../assets/images/LandingPage/getCertified.png';
import SchoolLogo1 from '../assets/images/LandingPage/IPlogo.png';
import LinkedIn from '../assets/images/LandingPage/LinkedIn.png';
import SchoolLogo5 from '../assets/images/LandingPage/MIlogo.png';
import SchoolLogo2 from '../assets/images/LandingPage/PATRIOTlogo.png';
import PersonalisedFeedbackImage from '../assets/images/LandingPage/personalisedFeedback.webp';
import PodiumImage from '../assets/images/LandingPage/podium.webp';
import PollImage from '../assets/images/LandingPage/poll.webp';
import SchoolLogo3 from '../assets/images/LandingPage/SANBENITOlogo.png';
import TikTok from '../assets/images/LandingPage/TikTok.png';
import Twitter from '../assets/images/LandingPage/twitter.png';
import WordcloudImage from '../assets/images/LandingPage/wordcloud.webp';
import theme from '../assets/theme';
import { Button } from '../components/_atoms/Button';
import VideoCarousel from '../components/_molecules/VideoCarousel';
import { useBreakPoint } from '../hooks/useBreakPoint';
import { useMarketingMetrics } from '../providers/PlausibleAnalyticsProvider';
import { getFullLessonGeneratorUrl } from '../utils/aiGeneratorUtils';

const UnauthenticatedHome = () => {
  return (
    <Container>
      <ExcitementForLearning />
      <MakeLessonsInSecondsVideos />
      <ManyStudents />
      <TeacherStory />
      <BecomeCertified />
      <ThousandsOfClassrooms />
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
`;

const SectionTitle = styled.h2`
  font-size: 30px;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;

  @media (max-width: 600px) {
    font-size: 24px;
  }
`;

const ExcitementForLearning = () => {
  const { t } = useTranslation('Host');
  const isLargerThan900 = useBreakPoint(900);
  return (
    <ActivitiesContainer>
      <CarouselContainer>
        <CarouselWrapper>
          <CarouselQuotes>
            <ActivityTitle>
              {t('excitementTogether', 'Excitement for learning, together! ')}
            </ActivityTitle>
            {isLargerThan900 && (
              <Link href={getFullLessonGeneratorUrl()} passHref>
                <a>
                  <Button
                    styles={{ backgroundColor: theme.colors.orange }}
                    icon={<MagicWandSvg />}
                  >
                    {t('Generate lessons with AI')}
                  </Button>
                </a>
              </Link>
            )}
          </CarouselQuotes>
          <Carousel
            emulateTouch={false}
            interval={5000}
            autoPlay={true}
            showArrows={false}
            showIndicators={false}
            infiniteLoop={true}
            showThumbs={true}
            showStatus={false}
            swipeable={true}
          >
            <div>
              <img
                src={PersonalisedFeedbackImage.src}
                alt={t('personalised feedback result')}
              />
            </div>
            <div>
              <img src={PodiumImage.src} alt={t('podium result')} />
            </div>
            <div>
              <img src={WordcloudImage.src} alt={t('wordcloud result')} />
            </div>
            <div>
              <img src={DrawingImage.src} alt={t('drawing result')} />
            </div>
            <div>
              <img src={PollImage.src} alt={t('poll result')} />
            </div>
          </Carousel>
        </CarouselWrapper>
      </CarouselContainer>
      {!isLargerThan900 && (
        <Link href={getFullLessonGeneratorUrl()} passHref>
          <a>
            <Button
              styles={{ backgroundColor: theme.colors.orange, marginBottom: '10px' }}
            >
              {' '}
              {t('Get started')}
            </Button>
          </a>
        </Link>
      )}
    </ActivitiesContainer>
  );
};
const ActivitiesContainer = styled.div`
  position: relative;
  padding: 20px;
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  height: 100%;
  background-color: ${theme.colors.green};
  overflow: hidden;

  &:-webkit-scrollbar {
    display: none;
  }
  * {
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    scrollbar-width: none; /* Firefox */
  }
  *::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;
const ActivityTitle = styled.h1`
  width: 100%;
  text-align: left;
  color: white;
  padding-left: 10%;
  margin-bottom: 40px;
  z-index: 1;
  font-size: 34px;

  @media (max-width: 900px) {
    margin-bottom: 20px;
    padding-left: 0;
    text-align: center;
  }
`;
const CarouselQuotes = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding-left: 20px;
  padding-right: 50px;
  padding-bottom: 40px;
  width: 100%;
  @media (max-width: 900px) {
    align-items: center;
    padding-right: 10px;
    padding-left: 10px;
    padding-bottom: 10px;
  }
  h1 {
    width: 100%;
    text-align: right;
    color: white;
    animation: fadeIn 0.5s;
    animation-name: text-fadein;
    @media (max-width: 900px) {
      text-align: center;
      margin-bottom: 20px;
    }
    @keyframes text-fadein {
      from {
        opacity: 0.7;
        margin-right: -20px;
      }
      to {
        opacity: 1;
        margin-left: 0;
      }
    }
  }
`;

const CarouselContainer = styled.div`
  width: 100%;
  background-color: ${theme.colors.green};
  max-width: 1000px;
  .axis-horizontal {
    border-radius: 10px;
    box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.3);
    @media (max-width: 00px) {
      padding: 0;
    }
  }
  .thumbs-wrapper.axis-vertical {
    margin-bottom: 0px;
    overflow-x: scroll;
    &:-webkit-scrollbar {
      display: none;
    }
    @media (max-width: 900px) {
      padding: 0;
      margin-top: 10px;
      margin-bottom: 10px;
      margin-left: 0px;
      margin-right: 0px;
      width: 100%;
      max-width: 540px;
      ul {
        padding: 0;
      }
    }

    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .thumbs,
  .thumbs.animated {
    padding: 0 !important;
    width: 100% !important;
    text-align: center;
  }

  .control-arrow.control-prev,
  .control-arrow.control-next {
    display: none;
  }
  .carousel,
  .carousel-root {
    width: 100%;
    max-width: 540px;
    margin: 0 auto;
  }

  .slide {
    border-radius: 5px;
    background-color: ${theme.colors.green};
  }
  .carousel-slider {
    overflow: visible;
  }

  .thumb {
    border: none;
    border-radius: 2px;
    padding: 0;
    background-color: ${theme.colors.background};
    cursor: pointer;
  }
  .thumb:hover {
    border: 2px solid #3e3e3e;
    box-shadow: none;
  }
  .thumb.selected {
    border: 2px solid ${theme.colors.orange};
    border-radius: 2px;
    box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.3);
  }
  h1 {
    color: white;
  }

  @media (max-width: 600px) {
    margin-bottom: 30px;
  }
`;
const CarouselWrapper = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  flex-wrap: nowrap;
  width: 100%;
  @media (max-width: 900px) {
    display: flex;
    flex-wrap: wrap;
  }
`;

const MakeLessonsInSecondsVideos = () => {
  const { track } = useMarketingMetrics();
  return (
    <MakeLessonsInSecondsVideosContainer>
      <SectionTitle>
        Plan and deliver interactive lessons on any topic - with help from AI
      </SectionTitle>
      <VideoCarousel />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginTop: 50,
          alignItems: 'center',
        }}
      >
        <p>See more reactions</p>
        <SoMeLinks>
          <Link href={'https://x.com/curipodofficial'} passHref>
            <a
              onClick={() => track('FrontPage.socialMediaClicked.x')}
              target="__blank"
              rel="noopener noreferrer"
            >
              <img src={Twitter.src} alt="X" />
            </a>
          </Link>
          <Link href={'https://www.tiktok.com/@curipod'} passHref>
            <a
              onClick={() => track('FrontPage.socialMediaClicked.tikTok')}
              target="__blank"
              rel="noopener noreferrer"
            >
              <img src={TikTok.src} alt="TikTok" />
            </a>
          </Link>
          <Link href={'https://www.linkedin.com/company/curipod'} passHref>
            <a
              onClick={() => track('FrontPage.socialMediaClicked.linkedIn')}
              target="__blank"
              rel="noopener noreferrer"
            >
              <img src={LinkedIn.src} alt="LinkedIn" />
            </a>
          </Link>
        </SoMeLinks>
      </div>
    </MakeLessonsInSecondsVideosContainer>
  );
};

const MakeLessonsInSecondsVideosContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.background};
  padding-top: 30px;
  padding-bottom: 40px;
  align-items: center;
  overflow: hidden;
  h2 {
    padding: 20px;
  }

  p {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 0;
  }

  @media (max-width: 900px) {
    flex-wrap: wrap-reverse;
    justify-content: center;
    padding-left: 5px;
    padding-top: 5px;
    padding-right: 5px;

    h2 {
      padding-top: 40px;
    }
  }
`;

const SoMeLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  img {
    width: 25px;
    height: 25px;
    margin: 10px;
    cursor: pointer;
  }
`;

const teacherStories = [
  {
    quote:
      'When did you last look forward to take your students on a learning journey with you?',
    excerpt:
      "I am teacher of 15 years who, until recently, has delved no more deeply into tech in the classroom, and here I am encouraging colleagues to come on in and see Curipod in action and leading CPD sessions. To top it off, I'm spending my evenings playing on my iPad to see how many new lessons I can create, and what new features have been added that week. The time saved, and the improvement in my lesson materials, is tangible.",
    link: '/blog/A Curipod Case Study By Aileen Wallace-329667c610dc40cf88bfc7e2eb207e7a',
    linkText: '- Read the full case study with Aileen Wallace',
  },
  {
    quote:
      'I have this young kid struggling, and she got so excited when the class pulled up her work!',
    excerpt:
      'I am a 30 year veteran of math education, and I found Curipod one night when I was looking for ways to increase student participation. I tried it the next day and I was hooked. The students were actually excited to use it! I LOVE that they each have their own whiteboard and that we can use their responses as great conversation starters. ',
    link: '',
    linkText: 'Gretchen Cuccio, Pearl R Miller Middle School',
  },
  {
    quote:
      'The learning activity I thought would take a few minutes, turned into a 45 minute discussion, where students were hitting all their learning targets',
    excerpt:
      'As an instructional technologist I can easily create lessons for my PDs and help teachers getting started. We get more creative with AI generated activities and the student engagement is substantially improved.',
    link: '',
    linkText: 'Stephanie Howell, Pickerington Local School District',
  },
];

const TeacherStory = () => {
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);

  return (
    <TeacherStoryContainer>
      <TeacherStoryWrapper>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            maxWidth: '600px',
            width: '100%',
          }}
        >
          <TeacherQuote>
            &ldquo;{teacherStories[currentStoryIndex].quote}&rdquo;
          </TeacherQuote>
        </div>
        <TeacherStoryDiv>
          <p style={{ textAlign: 'left' }}>{teacherStories[currentStoryIndex].excerpt}</p>
          {teacherStories[currentStoryIndex].link !== '' && (
            <Link href={teacherStories[currentStoryIndex].link}>
              {teacherStories[currentStoryIndex].linkText}
            </Link>
          )}
          {teacherStories[currentStoryIndex].link === '' && (
            <p style={{ fontWeight: 400, textAlign: 'left' }}>
              {teacherStories[currentStoryIndex].linkText}
            </p>
          )}
        </TeacherStoryDiv>
      </TeacherStoryWrapper>
      <Dots>
        <DotButton
          $isActive={currentStoryIndex === 0}
          onClick={() => setCurrentStoryIndex(0)}
        ></DotButton>
        <DotButton
          $isActive={currentStoryIndex === 1}
          onClick={() => setCurrentStoryIndex(1)}
        ></DotButton>
        <DotButton
          $isActive={currentStoryIndex === 2}
          onClick={() => setCurrentStoryIndex(2)}
        ></DotButton>
      </Dots>
    </TeacherStoryContainer>
  );
};

const TeacherStoryContainer = styled.div`
  height: 550px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: 1000px;
  margin: 0 auto;

  @media (min-width: 1000px) {
    height: 500px;
  }
  @media (max-width: 900px) {
    height: 650px;
  }
  @media (max-width: 550px) {
    height: 500px;
    padding: 0px 10px;
  }
  @media (max-width: 450px) {
    height: 660px;
  }
`;
const TeacherStoryWrapper = styled.div`
  padding: 20px;
  display: flex;
  background-color: ${theme.colors.background};
  padding-top: 50px;
  padding-bottom: 20px;
  flex-wrap: nowrap;
  align-items: start;
  justify-content: space-evenly;
  overflow: hidden;
  height: 100%;

  @media (max-width: 900px) {
    h1 {
      margin-top: 40px;
      margin-bottom: 0px;
    }
    flex-wrap: wrap;
    justify-content: center;
    padding-left: 5px;
    padding-top: 5px;
    padding-right: 5px;
  }
  @media (max-width: 550px) {
    h1 {
      font-size: 20px;
    }

    p {
      font-size: 16px !important;
    }
  }
`;

const TeacherStoryDiv = styled.div`
  padding: 10px;
  padding-left: 70px;
  padding-top: 10px;
  padding-bottom: 10px;
  max-width: 600px;
  width: 100%;

  p {
    font-size: 20px;
    font-weight: 300;
  }

  a {
    font-size: 20px;
    font-weight: 400;
  }

  a:hover {
    text-decoration: underline;
  }

  @media (max-width: 900px) {
    padding-left: 10px;
    h1,
    h3,
    p {
      margin-top: 30px;
      text-align: center;
    }
  }
`;

const TeacherQuote = styled.h1`
  padding: 10px 20px;
  font-style: italic;
  font-size: 30px;
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  width: 100%;
`;

const DotButton = styled(Button)<{ $isActive: boolean }>`
  visibility: visible !important;
  margin: 0 5px;
  width: 10px !important;
  height: 10px;
  border-radius: 50%;
  padding: 0px;
  background-color: ${theme.colors.greyDark};
  background-color: ${({ $isActive }) =>
    $isActive ? theme.colors.green : theme.colors.grey};
`;

const BecomeCertified = () => {
  const { t } = useTranslation('Host');
  const isLargerThen900 = useBreakPoint(900);
  const { track } = useMarketingMetrics();
  const { ref } = useParallax<HTMLImageElement>({
    translateX: ['50px', '100px'],
    rotate: ['0deg', '20deg'],
  });
  return (
    <div
      style={{
        width: '100%',
        background: theme.colors.green,
      }}
    >
      <BecomeCertifiedContainer>
        {!isLargerThen900 && (
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Link href={'/ai-certification'} passHref>
              <a>
                <Button
                  styles={{
                    backgroundColor: theme.colors.orange,
                    marginTop: '20px',
                    width: '220px',
                  }}
                  onClick={() => track('FrontPage.BecomeCertified.Clicked')}
                >
                  {t('Learn about ai certification')}
                </Button>
              </a>
            </Link>
          </div>
        )}
        <GetCertifiedDiv>
          <SectionTitle style={{ color: theme.colors.white }}>
            {t('Become an AI certified teacher')}
          </SectionTitle>
          <h3
            style={{
              width: '100%',
              fontWeight: 400,
            }}
          >
            {t(
              'LeadAChange',
              'Want to lead the the change in education, using AI to your advantage? Take our 10 hour certification course to earn your badge, and master using the AI in Curipod!',
            )}
          </h3>
          {isLargerThen900 && (
            <Link
              href={
                'https://curipod.notion.site/Curipod-Courses-8dcfbdfebd6d4b88a93c40c4aac9ce50'
              }
              passHref
            >
              <a>
                <Button
                  styles={{
                    backgroundColor: theme.colors.orange,
                    marginTop: '20px',
                    width: '220px',
                  }}
                  onClick={() => track('FrontPage.CuripodCourses.Clicked')}
                >
                  {t('Curipod Courses')}
                </Button>
              </a>
            </Link>
          )}
        </GetCertifiedDiv>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingRight: '100px',
          }}
        >
          <div
            style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              paddingBottom: '20px',
              marginLeft: '50px',
              width: '200px',
            }}
          >
            <img
              style={{ width: '100%', maxWidth: '150px', height: 'auto' }}
              src={GetCertified.src}
              alt="Curi-Nerd with glasses"
            />
            <img
              ref={ref}
              style={{
                maxWidth: '120px',
                height: 'auto',
                position: 'absolute',
                right: '0px',
                top: '55px ',
              }}
              src={Certificate.src}
              alt="Curipod certificate"
            />
          </div>
        </div>
      </BecomeCertifiedContainer>
    </div>
  );
};

const BecomeCertifiedContainer = styled.div`
  padding: 20px;
  display: flex;
  background-color: ${theme.colors.green};
  padding-top: 50px;
  padding-bottom: 40px;
  flex-wrap: nowrap;
  justify-content: space-evenly;
  overflow: hidden;
  max-width: 1000px;
  margin: 0 auto;

  h1,
  h3 {
    color: ${theme.colors.white};
  }

  img {
    height: 200px;
  }

  @media (max-width: 900px) {
    flex-wrap: wrap-reverse;
    justify-content: center;
    padding-left: 5px;
    padding-top: 5px;
    padding-right: 5px;

    img {
      margin-top: 50px;
    }
  }
`;

const GetCertifiedDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding-left: 20px;
  padding-right: 50px;
  width: 100%;

  h2 {
    text-align: left;
  }

  @media (max-width: 900px) {
    h2,
    h3 {
      margin: 0 auto;
      text-align: center;
      align-items: center;
    }
  }
`;

const ManyStudents = () => {
  const { ref } = useParallax<HTMLDivElement>({
    opacity: [0.2, 1.8],
  });
  const { ref: ref1 } = useParallax<HTMLDivElement>({
    opacity: [0.2, 1.8],
  });
  const { t } = useTranslation('Host');
  const { track } = useMarketingMetrics();
  return (
    <ManyStudentsContainer>
      <h1 style={{ width: '100%', textAlign: 'center' }} ref={ref}>
        + 4,000,000 {t('students')}{' '}
      </h1>

      <h2 style={{ width: '100%', textAlign: 'center' }} ref={ref1}>
        {t('have shared their voices')}
      </h2>

      <Link href={getFullLessonGeneratorUrl()} passHref>
        <a>
          <Button
            styles={{ backgroundColor: theme.colors.orange, marginTop: '20px' }}
            onClick={() => track('FrontPage.LetYourStudentsSpeek.Clicked')}
          >
            {t('Engage your students')}
          </Button>
        </a>
      </Link>
    </ManyStudentsContainer>
  );
};
const ManyStudentsContainer = styled.div`
  padding: 20px;
  min-height: 400px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  flex-wrap: wrap;

  background-color: ${theme.colors.green};
  h1 {
    font-size: 48px;
    margin: 0;
    color: ${theme.colors.white};
  }
  h2 {
    font-size: 24px;
    color: ${theme.colors.white};
  }
  @media (max-width: 900px) {
    h1 {
      font-size: 40px;
      margin: 0;
    }
    h2 {
      font-size: 20px;
    }
  }
`;

const ThousandsOfClassrooms = () => {
  const { t } = useTranslation('Host');
  const { track } = useMarketingMetrics();
  return (
    <ThousandsOfClassroomsContainer>
      <SectionTitle>{t('Curiosity sparked in thousands of classrooms')}</SectionTitle>
      <LogosGrid>
        <TopRow>
          <img src={SchoolLogo1.src} alt="... school logo" />
          <img src={SchoolLogo2.src} alt="... school logo" />
          <img src={SchoolLogo3.src} alt="... school logo" />
          <img src={SchoolLogo4.src} alt="... school logo" />
        </TopRow>
        <BottomRow>
          <img src={SchoolLogo5.src} alt="... school logo" />
          <img src={SchoolLogo6.src} alt="... school logo" />
          <img src={SchoolLogo7.src} alt="... school logo" />
        </BottomRow>
      </LogosGrid>
      <Link href="/pricing" passHref>
        <a>
          <Button
            styles={{ backgroundColor: theme.colors.orange, marginTop: '20px' }}
            onClick={() => track('FrontPage.organisationPlans.Clicked')}
          >
            {t('learnAboutPlans', 'Learn about organization plans')}
          </Button>
        </a>
      </Link>
    </ThousandsOfClassroomsContainer>
  );
};

const ThousandsOfClassroomsContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.background};
  padding-top: 50px;
  padding-bottom: 40px;
  align-items: center;
  overflow: hidden;

  @media (max-width: 900px) {
    flex-wrap: wrap-reverse;
    justify-content: center;
  }
`;

const LogosGrid = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-content: center;
  margin-bottom: 50px;
  align-items: center;
  gap: 20px;
  justify-items: center;
  padding-top: 20px;

  img {
    margin: 0 auto;
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TopRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  img {
    max-height: 100px;
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 550px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const BottomRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;
  img {
    max-height: 100px;
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    img:nth-child(3) {
      grid-column: 1 / 3;
    }
  }
  @media (max-width: 550px) {
    grid-template-columns: repeat(1, 1fr);
    img:nth-child(3) {
      grid-column: 1 / 2;
    }
  }
`;

export default UnauthenticatedHome;
